import { createMessageActionIcon } from './message-action-controls.js';

const AGENT_STEP_ICON = {
  response: '◈',
  tool_call: '⌕',
  tool_result: '▣',
  tool_error: '!',
  process: '…',
  fallback: '⚠',
  source_failure: '⚠',
  quality: '✓',
  context_load: '◎',
};

const AGENT_QUALITY_STATUS = new Set(['pass', 'fail', 'partial', 'unavailable']);

// AFV-003/007：这些步骤代表“本次回答受影响”，需提升为气泡顶部常驻告警条，不能只藏在折叠执行链里。
const FAILURE_STEP_KINDS = new Set(['tool_error', 'source_failure', 'fallback']);

function createElement(tagName, className = '', text = null) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== null) element.textContent = String(text);
  return element;
}

function getRenderableMessageContent(message) {
  const content = message?.content;
  if (String(message?.role || '').toUpperCase() === 'ASSISTANT' && /^(?:null|undefined)$/i.test(String(content ?? '').trim())) {
    return '';
  }
  return content || '';
}

function appendChildren(root, children) {
  root.append(...children.filter(Boolean));
  return root;
}

function appendInline(root, value) {
  const input = String(value ?? '');
  const token = /`([^`]+)`|\*\*(.+?)\*\*/g;
  let last = 0;
  let match;
  while ((match = token.exec(input))) {
    if (match.index > last) root.append(document.createTextNode(input.slice(last, match.index)));
    const tagName = match[1] !== undefined ? 'code' : 'strong';
    root.append(createElement(tagName, '', match[1] ?? match[2]));
    last = token.lastIndex;
  }
  if (last < input.length) root.append(document.createTextNode(input.slice(last)));
}

function createInlineElement(tagName, value, className = '') {
  const element = createElement(tagName, className);
  appendInline(element, value);
  return element;
}

function tableCells(line) {
  return String(line).trim().replace(/^\||\|$/g, '').split('|');
}

export function createMarkdownFragment(text) {
  const fragment = document.createDocumentFragment();
  if (!text) return fragment;
  const lines = String(text).replace(/\r\n/g, '\n').split('\n');
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const codeStart = line.match(/^```([^\s]*)\s*$/);
    if (codeStart) {
      const codeLines = [];
      index += 1;
      while (index < lines.length && !/^```\s*$/.test(lines[index])) codeLines.push(lines[index++]);
      if (index < lines.length) index += 1;
      const header = createElement('div', 'aichat-code-header');
      const copy = createElement('button', 'aichat-code-copy', '复制');
      copy.type = 'button';
      copy.dataset.action = 'copy-code-block';
      header.append(createElement('span', '', codeStart[1] || 'code'), copy);
      const code = createElement('code', '', codeLines.join('\n'));
      const pre = document.createElement('pre');
      pre.append(code);
      fragment.append(header, pre);
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      fragment.append(createInlineElement(`h${heading[1].length}`, heading[2]));
      index += 1;
      continue;
    }

    if (/^\s*(---+|\*\*\*+)\s*$/.test(line)) {
      fragment.append(document.createElement('hr'));
      index += 1;
      continue;
    }

    if (line.includes('|') && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1] || '')) {
      const headers = tableCells(line);
      index += 2;
      const rows = [];
      while (index < lines.length && lines[index].includes('|')) rows.push(tableCells(lines[index++]));
      const table = document.createElement('table');
      const headerRow = document.createElement('tr');
      headers.forEach((item) => headerRow.append(createInlineElement('th', item.trim())));
      const thead = document.createElement('thead');
      thead.append(headerRow);
      const tbody = document.createElement('tbody');
      rows.forEach((row) => {
        const tableRow = document.createElement('tr');
        headers.forEach((_, column) => tableRow.append(createInlineElement('td', String(row[column] || '').trim())));
        tbody.append(tableRow);
      });
      table.append(thead, tbody);
      fragment.append(appendChildren(createElement('div', 'aichat-markdown-table-wrap'), [table]));
      continue;
    }

    const unordered = /^\s*[-*+]\s+/.test(line);
    const ordered = /^\s*\d+[.)]\s+/.test(line);
    if (unordered || ordered) {
      const list = document.createElement(unordered ? 'ul' : 'ol');
      const marker = unordered ? /^\s*[-*+]\s+/ : /^\s*\d+[.)]\s+/;
      while (index < lines.length && marker.test(lines[index])) {
        list.append(createInlineElement('li', lines[index].replace(marker, '')));
        index += 1;
      }
      fragment.append(list);
      continue;
    }

    const paragraph = [line.trim()];
    index += 1;
    while (index < lines.length && lines[index].trim() && !/^(#{1,3})\s+|^```|^\s*[-*+]\s+|^\s*\d+[.)]\s+/.test(lines[index])) {
      paragraph.push(lines[index].trim());
      index += 1;
    }
    fragment.append(createInlineElement('p', paragraph.join(' ')));
  }

  return fragment;
}

function createTypingDots(inline = false) {
  const dots = createElement('span', `aichat-typing-dots${inline ? ' aichat-typing-dots--inline' : ''}`);
  dots.append(document.createElement('span'), document.createElement('span'), document.createElement('span'));
  return dots;
}

function normalizeToolResult(result) {
  if (typeof result !== 'string') return result == null ? '' : JSON.stringify(result);
  try {
    return JSON.stringify(JSON.parse(result), null, 2);
  } catch {
    return result;
  }
}

function createToolMessage(message, getToolMeta) {
  const toolName = String(message.toolData?.name || '');
  const isCall = message.contentType === 'TOOL_CALL';
  const isError = !isCall && String(message.toolData?.status || '').toUpperCase() === 'ERROR';
  const { icon, label } = getToolMeta(toolName, isCall);
  const card = createElement('div', `aichat-tool-card ${isCall ? 'aichat-tool-card--call' : 'aichat-tool-card--result'}${isError ? ' aichat-tool-card--error' : ''}`);
  card.append(
    createElement('span', 'aichat-tool-icon', isError ? '⚠️' : icon),
    createElement('span', 'aichat-tool-name', toolName),
    createElement('span', `aichat-tool-status${isError ? ' aichat-tool-status--error' : ''}`, isError ? '❌ 失败' : label),
  );

  if (isCall) {
    const args = message.toolData?.args ? JSON.stringify(message.toolData.args) : '';
    if (args) card.append(createElement('code', 'aichat-tool-args', args));
  } else if (isError && message.toolData?.errorMessage) {
    card.append(createElement('div', 'aichat-tool-error', message.toolData.errorMessage));
  } else {
    if (message.toolData?.latencyMs != null) card.append(createElement('span', 'aichat-tool-latency', `${message.toolData.latencyMs}ms`));
    const result = normalizeToolResult(message.toolData?.result);
    card.append(createElement('span', 'aichat-tool-result', result.length > 200 ? `${result.slice(0, 200)}...` : result));
  }

  return appendChildren(createElement('div', 'aichat-msg aichat-msg-tool'), [card]);
}

function createAgentQuality(quality) {
  const requestedStatus = String(quality?.status || 'unavailable').toLowerCase();
  const status = AGENT_QUALITY_STATUS.has(requestedStatus) ? requestedStatus : 'unavailable';
  const label = status === 'pass'
    ? '质量检查通过'
    : status === 'fail'
      ? '质量检查发现缺口'
      : status === 'partial'
        ? '质量检查部分完成'
        : '质量检查不可用';
  const score = Number.isFinite(Number(quality?.score)) ? ` · ${Math.round(Number(quality.score) * 100)}%` : '';
  const coverage = Number.isFinite(Number(quality?.coverage)) ? ` · 覆盖 ${Math.round(Number(quality.coverage) * 100)}%` : '';
  const chip = createElement('span', `aichat-agent-chip aichat-agent-chip--quality aichat-agent-chip--quality-${status}`, `${label}${score}`);
  chip.title = `S3 运行质量结果${coverage}`;
  return chip;
}

function createAgentStatus(message, formatAgentStateLabel) {
  if (!(message.agentState || message.agentSkills?.length || message.agentMemoryLoad?.length || message.agentQuality)) return null;
  const status = createElement('div', 'aichat-agent-status');
  if (message.agentState) status.append(createElement('span', 'aichat-agent-chip aichat-agent-chip--state', formatAgentStateLabel(message.agentState)));
  const skills = Array.isArray(message.agentSkills) ? message.agentSkills : [];
  skills.forEach((skill) => {
    const chip = createElement('span', 'aichat-agent-chip aichat-agent-chip--skill', `${skill.name || skill.skillId || 'skill'}${skill.version ? ` · v${skill.version}` : ''}`);
    chip.title = String(skill.skillId || '');
    status.append(chip);
  });
  (Array.isArray(message.agentMemoryLoad) ? message.agentMemoryLoad : []).slice(0, 3).forEach((memory) => {
    const text = String(memory);
    const chip = createElement('span', 'aichat-agent-chip aichat-agent-chip--memory', `🧠 ${text.slice(0, 40)}`);
    chip.title = text;
    status.append(chip);
  });
  if (message.agentQuality) status.append(createAgentQuality(message.agentQuality));

  const explanations = createElement('div', 'aichat-agent-skill-explanations');
  skills.forEach((skill) => {
    const tools = Array.isArray(skill.tools) && skill.tools.length ? skill.tools.join('、') : '无外部工具';
    const unavailable = Array.isArray(skill.unavailableTools) && skill.unavailableTools.length ? `；不可用：${skill.unavailableTools.join('、')}` : '';
    const versionSource = skill.versionSource === 'stable' ? '稳定版' : skill.versionSource === 'role_rollout' ? '角色灰度' : '用户灰度';
    explanations.append(appendChildren(createElement('div', 'aichat-agent-skill-explain'), [
      createElement('strong', '', skill.name || skill.skillId || '技能'),
      createElement('span', '', skill.purpose || '按当前任务调用该专家方法'),
      createElement('small', '', `工具边界：${tools + unavailable} · ${versionSource}`),
    ]));
  });
  if (skills.length) status.append(explanations);
  return status;
}

function formatAgentUsageMeta(message) {
  const usage = message?.agentUsage;
  const profile = message?.agentContextProfile;
  const parts = [];
  if (profile) parts.push(`策略 ${profile}`);
  if (usage && Number(usage.totalTokens) > 0) {
    const source = usage.source === 'provider' ? '实测' : usage.source === 'estimated' ? '估算' : '';
    parts.push(`Token ${usage.totalTokens}${source ? ` · ${source}` : ''}`);
  }
  if (message?.runId) parts.push(`Run ${String(message.runId).slice(0, 8)}`);
  return parts.join(' · ');
}

function createAgentTimeline(message, expanded) {
  const steps = Array.isArray(message.agentTimeline) ? message.agentTimeline : [];
  if (!steps.length) {
    if (!message.typing) return null;
    const activity = createElement('section', 'aichat-agent-activity');
    activity.setAttribute('aria-label', 'Agent 执行过程');
    activity.append(createElement('div', 'aichat-agent-activity__head', '正在处理 · 准备中'));
    return activity;
  }

  const activity = createElement('section', 'aichat-agent-activity');
  activity.setAttribute('aria-label', 'Agent 执行过程');
  const meta = formatAgentUsageMeta(message);
  activity.append(createElement('div', 'aichat-agent-activity__head', `${message.typing ? '正在处理' : '已处理'} · ${steps.length} 个步骤${meta ? ` · ${meta}` : ''}`));

  const evidence = steps.filter((step) => step.kind === 'tool_result' && step.summary).slice(0, 6);
  if (!message.typing && evidence.length) {
    const evidenceRoot = createElement('div', 'aichat-agent-activity__evidence');
    evidenceRoot.title = '证据路径';
    evidence.forEach((step) => evidenceRoot.append(createElement('span', 'aichat-agent-activity__chip', String(step.summary).slice(0, 80))));
    activity.append(evidenceRoot);
  }

  const list = createElement('ol', `aichat-agent-activity__list${expanded ? ' aichat-agent-activity__list--expanded' : ''}`);
  steps.forEach((step, index) => {
    const kind = Object.hasOwn(AGENT_STEP_ICON, step.kind) ? step.kind : 'process';
    const active = message.typing && index === steps.length - 1;
    const item = createElement('li', `aichat-agent-activity__item aichat-agent-activity__item--${kind}${active ? ' aichat-agent-activity__item--active' : ''}`);
    const content = createElement('span', 'aichat-agent-activity__content');
    content.append(createElement('strong', '', step.title || '处理步骤'));
    if (step.summary) content.append(createElement('small', '', step.summary));
    item.append(createElement('span', 'aichat-agent-activity__icon', AGENT_STEP_ICON[kind]), content);
    list.append(item);
  });
  activity.append(list);
  if (steps.length > 2) {
    const button = createElement('button', 'aichat-agent-activity__expand', expanded ? '收起执行链' : '展开执行链');
    button.type = 'button';
    button.dataset.action = 'toggle-agent-activity';
    button.dataset.msgId = String(message.id ?? '');
    activity.append(button);
  }
  return activity;
}

// 气泡顶部持久告警条：随时间线持久化，刷新后仍可回看失败原因与影响范围。
function createFailureNotices(message) {
  if (message.typing) return null;
  const steps = Array.isArray(message.agentTimeline) ? message.agentTimeline : [];
  const failures = steps.filter((step) => FAILURE_STEP_KINDS.has(step.kind));
  if (!failures.length) return null;
  const root = createElement('div', 'aichat-agent-notices');
  failures.slice(0, 3).forEach((step) => {
    const item = createElement('div', `aichat-agent-notice aichat-agent-notice--${step.kind}`);
    item.append(createElement('span', 'aichat-agent-notice__icon', '⚠'));
    const body = createElement('span', 'aichat-agent-notice__body');
    body.append(createElement('strong', '', step.title || '执行中出现问题'));
    if (step.summary) body.append(createElement('small', '', step.summary));
    item.append(body);
    root.append(item);
  });
  if (failures.length > 3) {
    root.append(createElement('div', 'aichat-agent-notice__more', `另有 ${failures.length - 3} 条告警，展开执行链查看详情`));
  }
  return root;
}

function createMessageAttachments(attachments) {
  if (!Array.isArray(attachments) || !attachments.length) return null;
  const root = createElement('div', 'aichat-msg-attachments');
  attachments.forEach((attachment) => {
    const label = attachment.originalName || attachment.filePath || '';
    if (String(attachment.mimeType || '').startsWith('image/')) {
      const image = document.createElement('img');
      image.className = 'aichat-msg-img';
      image.src = `/api/aichat/files/${encodeURI(String(attachment.filePath || ''))}`;
      image.alt = String(label);
      root.append(image);
    } else {
      root.append(createElement('div', 'aichat-msg-file', `📎 ${label}`));
    }
  });
  return root;
}

function createCopyMenu(messageId) {
  const wrapper = createElement('div', 'aichat-copy-wrapper');
  const toggle = createElement('button', 'aichat-msg-action-btn');
  toggle.type = 'button';
  toggle.dataset.action = 'copy-menu-toggle';
  toggle.append(createMessageActionIcon('copy'), document.createTextNode(' 复制 '), createElement('span', 'aichat-caret', '▾'));
  const menu = createElement('div', 'aichat-copy-menu');
  menu.hidden = true;
  const text = createElement('button', '', '纯文本');
  text.type = 'button';
  text.dataset.action = 'copy-text';
  const markdown = createElement('button', '', 'Markdown 源码');
  markdown.type = 'button';
  markdown.dataset.action = 'copy-markdown';
  markdown.dataset.msgId = messageId;
  menu.append(text, markdown);
  wrapper.append(toggle, menu);
  return wrapper;
}

function createAssistantActions(messageId, { hasContent, retryable }) {
  const actions = createElement('div', 'aichat-msg-actions');
  if (hasContent) actions.append(createCopyMenu(messageId));
  [
    ['regenerate', '重新生成', '重新生成这条回复', 'regenerate'],
    ...(retryable ? [
      ['edit-retry', '编辑后重试', '编辑原问题后重试', 'regenerate'],
      ['delete-retry', '删除后重试', '删除本轮并使用原问题重试', 'regenerate'],
    ] : []),
    ['up', '有用', '', 'up'],
    ['down', '有误', '', 'down'],
  ].forEach(([action, label, title, icon]) => {
    const button = createElement('button', 'aichat-msg-action-btn');
    button.type = 'button';
    button.dataset.action = action;
    button.dataset.msgId = messageId;
    if (title) button.title = title;
    button.append(createMessageActionIcon(icon), document.createTextNode(` ${label}`));
    actions.append(button);
  });
  return actions;
}

export function createMessageContentView(message, {
  avatarClass = 'aichat-avatar--assistant',
  avatarLetter = '',
  getToolMeta,
  formatAgentStateLabel = (value) => value,
  expandedAgentActivity = false,
} = {}) {
  if (message.contentType === 'TOOL_CALL' || message.contentType === 'TOOL_RESULT') {
    return createToolMessage(message, getToolMeta);
  }

  const fragment = document.createDocumentFragment();
  const isUser = message.role === 'USER';
  const messageId = String(message.id ?? '');
  const avatar = createElement('div', `aichat-avatar ${avatarClass}`, avatarLetter);
  const content = createElement('div', 'aichat-msg-content');
  content.dataset.msgId = messageId || 'msg-draft';
  const renderableContent = getRenderableMessageContent(message);
  const hasContent = Boolean(renderableContent);
  const retryable = !isUser && !message.typing
    && ['error', 'aborted', 'cancelled', 'failed'].includes(String(message.finishReason || '').toLowerCase())
    && Boolean(message.parentId);
  const canAct = !isUser && !message.typing && (hasContent || retryable);
  if (canAct) {
    const copy = createElement('button', 'aichat-copy-btn');
    copy.type = 'button';
    copy.title = '复制';
    copy.dataset.action = 'copy-bubble';
    copy.append(createMessageActionIcon('copy', { size: 14 }));
    content.append(copy);
  }
  if (message.typing && !hasContent) {
    content.append(createTypingDots());
  } else {
    content.append(createMarkdownFragment(renderableContent));
    if (!isUser && message.typing && hasContent) {
      const hint = createElement('div', 'aichat-process-hint');
      hint.append(createTypingDots(true), document.createTextNode(' 仍在处理，完整结论将写在此处…'));
      content.append(hint);
    }
  }

  const bubble = createElement('div', `aichat-msg ${isUser ? 'aichat-msg-user' : 'aichat-msg-assistant'}`);
  if (!isUser) {
    appendChildren(bubble, [
      createFailureNotices(message),
      createAgentStatus(message, formatAgentStateLabel),
      createAgentTimeline(message, expandedAgentActivity),
    ]);
  }
  appendChildren(bubble, [content, createMessageAttachments(message.attachments)]);
  fragment.append(appendChildren(createElement('div', 'aichat-msg-row'), [avatar, bubble]));
  if (canAct) fragment.append(createAssistantActions(messageId, { hasContent, retryable }));
  if (message.latencyMs != null || message.finishReason) {
    const meta = createElement('div', 'aichat-msg-meta');
    if (message.finishReason && message.finishReason !== 'STOP') meta.append(createElement('span', 'aichat-tag aichat-tag--stop', message.finishReason));
    if (message.latencyMs != null) meta.append(createElement('span', '', `${message.latencyMs}ms`));
    fragment.append(meta);
  }
  return fragment;
}
